import {
  resolveMediaAssetIdFromContentTopicPostMedia,
  resolveMediaAssetIdFromLibraryAsset,
  resolveParentMediaIdFromContentTopicPostMedia,
  resolveParentMediaIdFromLibraryAsset,
} from "../../lib/utils/mediaAssetId"
import { CONTENT_POST_STATUS, flattenTopicsForClient } from "../../store/slices/topicsSlice"

export const ALL_REVIEW_POST_STATUS_IDS = [
  CONTENT_POST_STATUS.DRAFT,
  CONTENT_POST_STATUS.SENT_FOR_APPROVAL,
  CONTENT_POST_STATUS.SENT_TO_CREATOR,
  CONTENT_POST_STATUS.SCHEDULED,
  CONTENT_POST_STATUS.POSTED,
  CONTENT_POST_STATUS.SENT_FOR_EXTERNAL_APPROVAL,
]

export const ASSET_FILENAME_MAX_LENGTH = 45

export const ASSET_IMAGE_ICON_SRC = "/assets/icons/common/img-icon.svg"
export const ASSET_VIDEO_ICON_SRC = "/assets/icons/common/video-play.svg"

export const RESOLUTION_DESCRIPTIONS = {
  "Ultra High": "Standout quality for hero posts or ads (4K+)",
  High: "Sharp visuals for most social feeds (2K–4K)",
  Standard: "Great for stories, carousels, daily content (1K–2K)",
  Low: "Lightweight previews, not for promotion (<1K)",
}

export const PAST_POSTS_DESCRIPTIONS = {
  "Fresh Assets": "Not yet used, ready for first campaign",
  "Frequently Used": "Already used in past posts",
}

const PAST_POSTS_TRAILING_GROUPS = {
  "frequently used": 1,
  "fresh assets": 2,
  others: 3,
}

export const isVideoAsset = (media) => {
  if (!media) return false
  if (media.media_type_id === 2 || media.typeId === 2) return true
  if (media.dam_metadata?.media_type?.primary_type === "video") return true
  const mime = media.mimeType || media.mime_type || media.mediaAssets?.[0]?.mimeType
  if (typeof mime === "string" && mime.startsWith("video/")) return true
  const url = getVideoSource(media)
  const filename = media.asset_filename || media.assetFilename || ""
  return inferIsVideoFromUrlOrName(url, filename, media.media_type_id ?? media.typeId)
}

export const getVideoSource = (media) => {
  if (!media) return ""
  return (
    media.mediaAssets?.[0]?.asset_url ||
    media.mediaAssets?.[0]?.url ||
    media.assetUrl ||
    media.asset_url ||
    media.video_url ||
    media.streaming_url ||
    media.proxy_url ||
    ""
  )
}

const pickFirstUrl = (...candidates) => {
  for (const value of candidates) {
    if (value != null && String(value).trim() !== "") return String(value).trim()
  }
  return ""
}

const isVideoUrl = (url) =>
  typeof url === "string" && /\.(mp4|mov|webm|avi|mkv|qt|m4v)(\?|$)/i.test(url)

/** Detect video from URL, filename, or API media type id (2 = video). */
export const inferIsVideoFromUrlOrName = (url, filename, mediaTypeId) => {
  if (mediaTypeId === 2 || mediaTypeId === 15) return true
  return isVideoUrl(url) || isVideoUrl(filename)
}

const imageMimeOrExt = (value) => {
  const text = String(value || "").trim().toLowerCase()
  if (!text) return false
  if (text.startsWith("image/")) return true
  return /\.(jpe?g|png|gif|webp|bmp|avif)(\?|$)/i.test(text)
}

const urlLooksLikeImage = (url) => {
  const text = String(url || "").trim()
  if (!text || isVideoUrl(text)) return false
  return imageMimeOrExt(text)
}

/** Still image / poster for video cards (not the playable file). */
export const getVideoThumbnail = (media) => {
  if (!media) return ""
  const asset = media.mediaAssets?.[0]
  const proxy = pickFirstUrl(media.proxy_url, asset?.proxy_url, media.proxyUrl, asset?.proxyUrl)
  const proxyAsThumb = proxy && !isVideoUrl(proxy) ? proxy : ""
  const flatAssetUrl = pickFirstUrl(
    media.assetUrl,
    media.asset_url,
    asset?.assetUrl,
    asset?.url,
    asset?.asset_url,
  )
  const assetUrlAsThumb =
    flatAssetUrl && urlLooksLikeImage(flatAssetUrl) ? flatAssetUrl : ""
  const assetMimeThumb =
    asset?.mimeType && imageMimeOrExt(asset.mimeType)
      ? pickFirstUrl(asset.url, asset.asset_url, asset.thumbnail_url, asset.thumbnailUrl)
      : ""

  return pickFirstUrl(
    media.thumbnail_url,
    media.thumbnailUrl,
    asset?.thumbnail_url,
    asset?.thumbnailUrl,
    media.poster_url,
    media.posterUrl,
    asset?.poster_url,
    asset?.posterUrl,
    media.cover_url,
    media.coverUrl,
    asset?.cover_url,
    asset?.coverUrl,
    media.preview_url,
    media.previewUrl,
    asset?.preview_url,
    asset?.previewUrl,
    assetUrlAsThumb,
    assetMimeThumb,
    proxyAsThumb,
  )
}

export const VIDEO_THUMB_PLACEHOLDER = "/assets/icons/common/multi-video.svg"

/** Grid/list preview for images; prefer lightweight proxy/thumbnail URLs over full origin assets. */
export const getImagePreviewUrl = (media) => {
  if (!media) return "/placeholder.svg"
  const asset = media.mediaAssets?.[0]
  const url = pickFirstUrl(
    media.thumbnail_url,
    media.thumbnailUrl,
    asset?.thumbnail_url,
    asset?.thumbnailUrl,
    media.proxy_url,
    asset?.proxy_url,
    media.preview_url,
    media.previewUrl,
    asset?.preview_url,
    asset?.previewUrl,
    media.asset_url,
    asset?.asset_url,
  )
  if (!url) return "/placeholder.svg"
  if (isVideoUrl(url)) return "/placeholder.svg"
  return url
}

export const formatCategoryName = (cat) => {
  if (!cat) return "Others"
  let value = ""
  if (typeof cat === "string") value = cat
  else if (Array.isArray(cat)) value = cat[0] || ""
  else if (typeof cat === "object") {
    value = cat.name || cat.label || cat.category || cat.description || ""
  }
  if (typeof value !== "string" || !value.trim()) return "Others"
  const formatted = value
    .split(" ")
    .map((word) => (word ? word.charAt(0).toUpperCase() + word.slice(1).toLowerCase() : ""))
    .join(" ")
  return formatted.replace(/&([a-z])/g, (_, letter) => `&${letter.toUpperCase()}`)
}

export const normalizeCategoryLabel = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()

export const isOthersCategoryGroup = (groupName) =>
  normalizeCategoryLabel(groupName) === "others"

/** View-by group keys: named groups A→Z, then Frequently Used, Fresh Assets, Others. */
export const sortGroupKeysWithOthersLast = (keys) => {
  if (!Array.isArray(keys) || keys.length <= 1) return keys ? [...keys] : []
  const trailing = []
  const rest = []
  for (const key of keys) {
    if (PAST_POSTS_TRAILING_GROUPS[normalizeCategoryLabel(key)] != null) trailing.push(key)
    else rest.push(key)
  }
  rest.sort((a, b) => String(a).localeCompare(String(b), undefined, { sensitivity: "base" }))
  trailing.sort(
    (a, b) =>
      PAST_POSTS_TRAILING_GROUPS[normalizeCategoryLabel(a)] -
      PAST_POSTS_TRAILING_GROUPS[normalizeCategoryLabel(b)],
  )
  return [...rest, ...trailing]
}

/** True when media belongs to the given view-by group (Category, Performance, Resolution, etc.). */
export const mediaMatchesViewGroup = (media, groupName, viewBy = "Category") => {
  if (!media || !groupName) return false
  return (
    normalizeCategoryLabel(getMediaGroupKey(media, viewBy)) ===
    normalizeCategoryLabel(groupName)
  )
}

/** @deprecated Use mediaMatchesViewGroup */
export const mediaMatchesCategory = (media, categoryName) =>
  mediaMatchesViewGroup(media, categoryName, "Category")

function mediaUsageCount(media) {
  const n = Number(media?.used_count ?? media?.usage_count ?? 0)
  return Number.isFinite(n) ? n : 0
}

function getPastPostCampaignName(media) {
  const sources = [
    media?.campaigns,
    media?.content_topics,
    media?.related_posts,
    media?.post_history,
    media?.content_analysis?.campaigns,
    media?.content_analysis?.campaign_names,
  ]
  for (const arr of sources) {
    if (!Array.isArray(arr) || arr.length === 0) continue
    for (const entry of arr) {
      let name = ""
      if (typeof entry === "string") name = entry
      else if (entry?.campaign_name) name = entry.campaign_name
      else if (entry?.name) name = entry.name
      else if (entry?.title) name = entry.title
      name = String(name).trim()
      if (name) return formatCategoryName(name)
    }
  }
  const single =
    media?.campaign_name ||
    media?.content_analysis?.campaign_name ||
    media?.content_topic_name
  if (single && String(single).trim()) return formatCategoryName(single)
  return ""
}

export const getMediaGroupKey = (media, viewBy) => {
  if (!media) return "Others"

  if (viewBy === "Category") {
    const raw =
      media.content_analysis?.primary_category ||
      media.primary_category ||
      media.media_metadata?.primary_category
    if (raw) {
      return formatCategoryName(raw)
    }
    return "Others"
  }

  if (viewBy === "Resolution") {
    if (media.media_metadata?.resolution_category) {
      const resolutionMap = {
        low: "Low",
        standard: "Standard",
        high: "High",
        ultra: "Ultra High",
      }
      return resolutionMap[media.media_metadata.resolution_category] || "Standard"
    }
    return "Standard"
  }

  if (viewBy === "Media type") {
    return isVideoAsset(media) ? "Videos" : "Images"
  }

  if (viewBy === "Performance") {
    return media.content_analysis?.estimated_dimensions?.category || "High Performance"
  }

  if (viewBy === "Social Tags") {
    if (media.visual_tags?.length > 0) {
      return formatCategoryName(media.visual_tags[0])
    }
    return "Others"
  }

  if (viewBy === "Past Posts" || viewBy === "Past Campaigns") {
    const campaignName = getPastPostCampaignName(media)
    if (campaignName) return campaignName
    if (mediaUsageCount(media) <= 0) return "Fresh Assets"
    return "Frequently Used"
  }

  return "Others"
}

export const truncateFilename = (name) => {
  if (!name || typeof name !== "string") return ""
  return name.length > ASSET_FILENAME_MAX_LENGTH
    ? `${name.slice(0, ASSET_FILENAME_MAX_LENGTH)}…`
    : name
}

export const getTimeAgo = (dateString) => {
  if (!dateString) return ""
  const safeDate = dateString.endsWith("Z") ? dateString : `${dateString}Z`
  const date = new Date(safeDate)
  const now = new Date()
  const seconds = Math.floor((now - date) / 1000)
  let interval = seconds / 31536000
  if (interval > 1) return `${Math.floor(interval)} years ago`
  interval = seconds / 2592000
  if (interval > 1) return `${Math.floor(interval)} months ago`
  interval = seconds / 86400
  if (interval > 1) return `${Math.floor(interval)} days ago`
  interval = seconds / 3600
  if (interval > 1) return `${Math.floor(interval)} hours ago`
  interval = seconds / 60
  if (interval > 1) return `${Math.floor(interval)} minutes ago`
  return `${Math.floor(seconds)} seconds ago`
}

export const getPerformanceLabel = (media) => {
  const fromAnalysis = media?.content_analysis?.estimated_dimensions?.category
  if (fromAnalysis) return fromAnalysis
  const score = Number(media?.performance_score)
  if (!Number.isFinite(score)) return null
  if (score >= 67) return "High performance"
  if (score >= 34) return "Medium performance"
  return "Low performance"
}

export const isHighPerformanceLabel = (label) =>
  Boolean(label && String(label).toLowerCase().includes("high"))

/** Shared sort labels — AI Asset Manager page and create-post asset picker modal. */
export const ASSET_MANAGER_SORT_OPTIONS = [
  "Last Uploaded (New to Old)",
  "Most Used",
  "Least Used",
  "Alphabetical (A-Z)",
  "Alphabetical (Z-A)",
]

const MEDIA_SEARCH_NAME_KEYS = [
  "campaign_name",
  "campaignName",
  "content_topic_name",
  "contentTopicName",
  "topic_name",
  "topicName",
  "post_name",
  "postName",
  "post_title",
  "postTitle",
  "post_copy",
  "postCopy",
  "caption",
  "title",
  "name",
  "label",
]

function appendSearchStrings(value, parts, depth = 0) {
  if (value == null || depth > 3) return
  if (typeof value === "string") {
    const text = value.trim()
    if (text) parts.push(text)
    return
  }
  if (Array.isArray(value)) {
    value.forEach((item) => appendSearchStrings(item, parts, depth + 1))
    return
  }
  if (typeof value === "object") {
    MEDIA_SEARCH_NAME_KEYS.forEach((key) => appendSearchStrings(value[key], parts, depth + 1))
  }
}

function collectMediaSearchParts(media) {
  if (!media) return []
  const parts = []
  appendSearchStrings(media.asset_filename, parts)
  appendSearchStrings(media.alt_text, parts)
  appendSearchStrings(media.short_description, parts)
  appendSearchStrings(media.primary_category, parts)
  appendSearchStrings(media.content_analysis?.primary_category, parts)
  appendSearchStrings(media.content_analysis?.style_or_mood, parts)
  appendSearchStrings(media.visual_tags, parts)
  appendSearchStrings(media.descriptive_tags, parts)
  appendSearchStrings(media.semantic_keywords, parts)
  appendSearchStrings(media.post_copy ?? media.postCopy, parts)
  appendSearchStrings(media.content_analysis?.post_copy, parts)
  appendSearchStrings(media.campaign_name, parts)
  appendSearchStrings(media.content_topic_name, parts)
  appendSearchStrings(media.topic_name, parts)
  appendSearchStrings(media.post_name, parts)
  appendSearchStrings(media.title, parts)
  appendSearchStrings(media.campaigns, parts)
  appendSearchStrings(media.content_topics, parts)
  appendSearchStrings(media.related_posts, parts)
  appendSearchStrings(media.post_history, parts)
  appendSearchStrings(media.content_analysis?.campaigns, parts)
  appendSearchStrings(media.content_analysis?.campaign_names, parts)
  appendSearchStrings(media.content_analysis?.post_titles, parts)
  appendSearchStrings(media.content_analysis?.post_name, parts)
  const damTags = media.dam_metadata?.tags
  if (damTags && typeof damTags === "object") {
    Object.values(damTags).forEach((value) => appendSearchStrings(value, parts))
  }
  return parts
}

function collectMediaPostNames(media) {
  if (!media) return []
  const parts = []
  appendSearchStrings(media.campaign_name, parts)
  appendSearchStrings(media.content_topic_name, parts)
  appendSearchStrings(media.topic_name, parts)
  appendSearchStrings(media.post_name, parts)
  appendSearchStrings(media.title, parts)
  appendSearchStrings(media.campaigns, parts)
  appendSearchStrings(media.content_topics, parts)
  appendSearchStrings(media.related_posts, parts)
  appendSearchStrings(media.post_history, parts)
  appendSearchStrings(media.content_analysis?.campaigns, parts)
  appendSearchStrings(media.content_analysis?.campaign_names, parts)
  appendSearchStrings(media.content_analysis?.post_titles, parts)
  appendSearchStrings(media.content_analysis?.post_name, parts)
  return parts
}

function normalizeSearchText(value) {
  return String(value || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
}

export const mediaMatchesSearchTerm = (media, searchTerm) => {
  const q = normalizeSearchText(searchTerm)
  if (!q) return true
  const haystack = normalizeSearchText(collectMediaSearchParts(media).join(" "))
  if (!haystack) return false
  if (haystack.includes(q)) return true
  const tokens = q.split(" ").filter((token) => token.length > 1)
  return tokens.length > 0 && tokens.every((token) => haystack.includes(token))
}

export const getLibraryMediaKey = (media, index = 0) =>
  String(media?.id_media ?? media?.media_asset_id ?? media?.id_asset ?? `row:${index}`)

export const libraryMediaIdSet = (medias) => {
  const ids = new Set()
  if (!Array.isArray(medias)) return ids
  medias.forEach((media, index) => {
    if (!media) return
    ids.add(getLibraryMediaKey(media, index))
    if (media.id_media != null) ids.add(String(media.id_media))
    if (media.media_asset_id != null) ids.add(String(media.media_asset_id))
    if (media.id_asset != null) ids.add(String(media.id_asset))
  })
  return ids
}

export const mergeMediaListsById = (...lists) => {
  const byId = new Map()
  lists.forEach((list) => {
    if (!Array.isArray(list)) return
    list.forEach((media, index) => {
      if (!media) return
      const key = getLibraryMediaKey(media, index)
      if (!byId.has(key)) byId.set(key, media)
    })
  })
  return [...byId.values()]
}

/** Full library while idle; typeahead results only after that search succeeds. */
export const mediasForSearchQuery = ({ medias, searchTerm, snapshotRef, mediasStatus, mediasQuery }) => {
  const current = Array.isArray(medias) ? medias : []
  const q = String(searchTerm || "").trim()
  if (!q) {
    if (current.length && snapshotRef) snapshotRef.current = current
    return current
  }
  if (mediasStatus === "succeeded" && String(mediasQuery || "").trim() === q) {
    return current
  }
  if (snapshotRef && !snapshotRef.current.length && current.length) {
    snapshotRef.current = current
  }
  return mergeMediaListsById(snapshotRef?.current, current)
}

function collectTopicPostSearchNames(topic, depth = 0) {
  if (!topic || depth > 2) return []
  const parts = []
  appendSearchStrings(topic.name, parts)
  appendSearchStrings(topic.campaignName, parts)
  appendSearchStrings(topic.contentTopicTitle, parts)
  const posts = [
    ...(Array.isArray(topic.contentTopicPosts) ? topic.contentTopicPosts : []),
    topic.firstPost,
  ]
  posts.forEach((post) => {
    if (!post) return
    appendSearchStrings(post.name, parts)
    appendSearchStrings(post.postName, parts)
    appendSearchStrings(post.title, parts)
    appendSearchStrings(post.topicName, parts)
  })
  if (topic.originalTopic && topic.originalTopic !== topic) {
    collectTopicPostSearchNames(topic.originalTopic, depth + 1).forEach((name) => parts.push(name))
  }
  return parts
}

function collectTopicPostMediaIdKeys(topic) {
  const keys = []
  const seen = new Set()
  const push = (id) => {
    if (id == null || id === "") return
    const key = String(id)
    if (seen.has(key)) return
    seen.add(key)
    keys.push(key)
  }
  const posts = [
    ...(Array.isArray(topic?.contentTopicPosts) ? topic.contentTopicPosts : []),
    topic?.firstPost,
    ...(Array.isArray(topic?.originalTopic?.contentTopicPosts) ? topic.originalTopic.contentTopicPosts : []),
  ]
  posts.forEach((post) => {
    if (!post) return
    const rows = Array.isArray(post.contentTopicPostMedias) ? post.contentTopicPostMedias : []
    rows.forEach((row) => {
      push(resolveParentMediaIdFromContentTopicPostMedia(row, post))
      push(resolveMediaAssetIdFromContentTopicPostMedia(row))
    })
    push(post.mediaId)
    push(post.media_id)
  })
  return keys
}

export const reviewTopicsFromState = (topicsState, clientId) => {
  const fromMonth = flattenTopicsForClient(topicsState?.topicsByMonth, clientId)
  const list = Array.isArray(topicsState?.topics) ? topicsState.topics : []
  const processed = Object.values(topicsState?.processedTopics || {}).flat()
  const byId = new Map()
  const add = (topic) => {
    if (!topic) return
    const raw = topic.originalTopic || topic
    const id = raw?.id ?? topic?.id
    const key = id != null ? String(id) : `anon:${byId.size}`
    const existing = byId.get(key)
    byId.set(key, {
      ...(existing || {}),
      ...raw,
      campaignName: topic.campaignName || raw.campaignName || raw.name,
      contentTopicPosts: raw.contentTopicPosts || existing?.contentTopicPosts || topic.contentTopicPosts,
    })
  }
  fromMonth.forEach(add)
  list.forEach(add)
  processed.forEach(add)
  return [...byId.values()]
}

export const collectReviewPostMediaIdSet = (topics, searchTerm) => {
  const q = String(searchTerm || "").trim().toLowerCase()
  const ids = new Set()
  if (!q || !Array.isArray(topics) || topics.length === 0) return ids
  topics.forEach((topic) => {
    const matches = collectTopicPostSearchNames(topic).some((name) => name.toLowerCase().includes(q))
    if (!matches) return
    collectTopicPostMediaIdKeys(topic).forEach((id) => ids.add(id))
  })
  return ids
}

export const libraryMediaMatchesIdSet = (media, idSet) => {
  if (!media || !idSet || idSet.size === 0) return false
  const candidates = [
    resolveParentMediaIdFromLibraryAsset(media),
    resolveMediaAssetIdFromLibraryAsset(media),
    media.id_media,
    media.media_asset_id,
    media.id_asset,
  ]
  return candidates.some((id) => id != null && id !== "" && idSet.has(String(id)))
}

export const filterAndSortMedias = (userMedias, filters) => {
  if (!userMedias || userMedias.length === 0) return []

  const {
    searchTerm = "",
    resolutionFilter = [],
    mediaTypeFilter = "",
    recentActivity = "",
    approvalStatus = "",
    socialTags = [],
    performanceFilter = [],
    sortBy = "Last Uploaded (New to Old)",
    reviewMediaIds,
    typeaheadIdSet,
  } = filters

  let filtered = [...userMedias]

  if (searchTerm && searchTerm.trim().length > 0 && !typeaheadIdSet) {
    filtered = filtered.filter(
      (media) =>
        mediaMatchesSearchTerm(media, searchTerm) || libraryMediaMatchesIdSet(media, reviewMediaIds),
    )
  }

  if (resolutionFilter.length > 0) {
    filtered = filtered.filter((media) => {
      const raw = media.media_metadata?.resolution_category
      if (!raw) return false
      const normalized = raw.toString().toLowerCase()
      if (normalized.includes("low")) return resolutionFilter.includes("low")
      if (normalized.includes("standard")) return resolutionFilter.includes("standard")
      if (normalized.includes("high") && !normalized.includes("ultra")) return resolutionFilter.includes("high")
      if (normalized.includes("ultra")) return resolutionFilter.includes("ultra")
      return false
    })
  }

  if (mediaTypeFilter === "Images") {
    filtered = filtered.filter((media) => !isVideoAsset(media))
  }
  if (mediaTypeFilter === "Videos") {
    filtered = filtered.filter((media) => isVideoAsset(media))
  }

  if (recentActivity) {
    const now = new Date()
    let daysAgo = 0
    if (recentActivity === "Last 7 days") daysAgo = 7
    else if (recentActivity === "Last 2 weeks") daysAgo = 14
    else if (recentActivity === "Last 1 month") daysAgo = 30
    else if (recentActivity === "Last 3 months") daysAgo = 90
    else if (recentActivity === "Last 6 months") daysAgo = 180
    else if (recentActivity === "Last 1 year") daysAgo = 365

    if (daysAgo > 0) {
      const cutoffDate = new Date(now.getTime() - daysAgo * 24 * 60 * 60 * 1000)
      filtered = filtered.filter((media) => {
        const insertDate = new Date(media.inserted_datetime || media.inserted_datetime_media)
        return insertDate >= cutoffDate
      })
    }
  }

  if (socialTags.length > 0) {
    filtered = filtered.filter((media) => {
      const visualTags = media.visual_tags || []
      const descriptiveTags = media.descriptive_tags || []
      const allTags = [...visualTags, ...descriptiveTags].map((tag) => tag.toLowerCase())
      return socialTags.some((selectedTag) =>
        allTags.some((tag) => tag.includes(selectedTag.toLowerCase())),
      )
    })
  }

  if (approvalStatus) {
    filtered = filtered.filter((media) => {
      const st = String(media.dam_metadata?.approval_status || "").trim()
      if (approvalStatus === "Approved") return st === "Approved"
      if (approvalStatus === "Pending Approval") return st !== "Approved"
      return true
    })
  }

  if (performanceFilter.length > 0) {
    filtered = filtered.filter((media) => {
      const score = Number(media.performance_score)
      const tier = !Number.isFinite(score) ? "Low" : score >= 67 ? "High" : score >= 34 ? "Medium" : "Low"
      return performanceFilter.includes(tier)
    })
  }

  if (sortBy === "Last Uploaded (New to Old)") {
    filtered.sort((a, b) => {
      const dateA = new Date(a.inserted_datetime || a.inserted_datetime_media || 0)
      const dateB = new Date(b.inserted_datetime || b.inserted_datetime_media || 0)
      return dateB - dateA
    })
  } else if (sortBy === "Most Used") {
    filtered.sort((a, b) => (b.used_count ?? 0) - (a.used_count ?? 0))
  } else if (sortBy === "Least Used") {
    filtered.sort((a, b) => (a.used_count ?? 0) - (b.used_count ?? 0))
  } else if (sortBy === "Performance - high to low") {
    filtered.sort((a, b) => (b.performance_score || 0) - (a.performance_score || 0))
  } else if (sortBy === "Performance - low to high") {
    filtered.sort((a, b) => (a.performance_score || 0) - (b.performance_score || 0))
  } else if (sortBy === "Alphabetical (A-Z)") {
    filtered.sort((a, b) =>
      (a.asset_filename || "").toLowerCase().localeCompare((b.asset_filename || "").toLowerCase()),
    )
  } else if (sortBy === "Alphabetical (Z-A)") {
    filtered.sort((a, b) =>
      (b.asset_filename || "").toLowerCase().localeCompare((a.asset_filename || "").toLowerCase()),
    )
  }

  return filtered
}

/** Post Muse / social caption from media library API (`post_copy` on each result). */
export const getMediaPostCopy = (media) => {
  if (!media) return ""

  const topLevel = media.post_copy ?? media.postCopy
  if (typeof topLevel === "string" && topLevel.trim()) return topLevel.trim()

  const nested = media.content_analysis?.post_copy
  if (typeof nested === "string" && nested.trim()) return nested.trim()
  if (Array.isArray(nested)) {
    const first = nested.find((item) => typeof item === "string" && item.trim())
    if (first) return first.trim()
  }

  return ""
}

export const buildSmartSearchSuggestions = (medias) => {
  if (!medias || medias.length === 0) return []

  const keywordsSet = new Set()

  medias.forEach((media) => {
    media.semantic_keywords?.forEach((kw) => {
      if (kw && kw.length > 3) keywordsSet.add(kw.toLowerCase())
    })

    media.visual_tags?.forEach((tag) => {
      if (tag && tag.length > 3) keywordsSet.add(tag.toLowerCase())
    })

    media.descriptive_tags?.forEach((tag) => {
      if (tag && tag.length > 3) keywordsSet.add(tag.toLowerCase())
    })

    const category = media.content_analysis?.primary_category
    if (category && category.length > 3) keywordsSet.add(category.toLowerCase())

    if (media.asset_filename) {
      media.asset_filename
        .replace(/\.[^/.]+$/, "")
        .split(/[\s_-]+/)
        .forEach((word) => {
          if (word.length > 3) keywordsSet.add(word.toLowerCase())
        })
    }

    collectMediaPostNames(media).forEach((name) => {
      if (name && name.length > 3) keywordsSet.add(name.toLowerCase())
    })
  })

  return Array.from(keywordsSet)
    .slice(0, 8)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
}

export const formatResolutionPill = (res) => {
  const map = { low: "Low", standard: "Standard", high: "HD", ultra: "UHD" }
  return map[res] || (res ? res.charAt(0).toUpperCase() + res.slice(1) : "")
}

export const FOCUS_UPLOADED_ASSETS_KEY = "focusUploadedAssets"

export const getAssetFocusDataProps = (asset) => {
  const id = asset?.media_asset_id ?? asset?.id_asset ?? asset?.mediaAssets?.[0]?.id
  const name = asset?.asset_filename || asset?.assetFilename || ""
  return {
    ...(id != null && id !== "" ? { "data-media-asset-id": String(id) } : {}),
    ...(name ? { "data-asset-filename": name } : {}),
  }
}

const mediaCategoryValue = (media) =>
  media?.content_analysis?.primary_category ||
  media?.primary_category ||
  media?.media_metadata?.primary_category

/** Drop same-id rows and hide only the files still being organized. */
export const visibleLibraryMedias = (medias, assetUpload) => {
  if (!Array.isArray(medias) || medias.length === 0) return []

  const byId = new Map()
  medias.forEach((media, index) => {
    const key = String(media?.id_media ?? media?.media_asset_id ?? media?.id_asset ?? `row:${index}`)
    const existing = byId.get(key)
    if (!existing || (!mediaCategoryValue(existing) && mediaCategoryValue(media))) {
      byId.set(key, media)
    }
  })
  const list = [...byId.values()]

  const phase = assetUpload?.phase
  if (phase !== "uploading" && phase !== "processing") return list

  const organizingIds = new Set(
    (assetUpload?.files || [])
      .map((file) => file?.mediaAssetId)
      .filter((id) => id != null && id !== "")
      .map(String),
  )
  if (!organizingIds.size) return list

  return list.filter((media) => {
    const assetId = media?.media_asset_id ?? media?.id_asset ?? media?.mediaAssets?.[0]?.id
    return assetId == null || !organizingIds.has(String(assetId))
  })
}
